import React, { useState, useEffect } from 'react';

interface OfflineIndicatorProps {
  className?: string;
  showWhenOnline?: boolean;
}

export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({ 
  className = '',
  showWhenOnline = false
}) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setWasOffline(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Don't show anything if online and we don't want to show online status
  if (isOnline && !showWhenOnline && !wasOffline) {
    return null;
  }

  // Show reconnection message briefly when coming back online
  if (isOnline && wasOffline) {
    setTimeout(() => setWasOffline(false), 3000);
  }

  const baseStyles: React.CSSProperties = {
    position: 'fixed',
    top: '10px',
    right: '10px',
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  };

  const offlineStyles: React.CSSProperties = {
    ...baseStyles,
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    border: '1px solid #fecaca'
  };

  const onlineStyles: React.CSSProperties = {
    ...baseStyles,
    backgroundColor: '#dcfce7',
    color: '#16a34a',
    border: '1px solid #bbf7d0'
  };

  const StatusIcon: React.FC<{ isOnline: boolean }> = ({ isOnline }) => (
    <div
      style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: isOnline ? '#16a34a' : '#dc2626'
      }}
      aria-hidden="true"
    />
  );

  if (!isOnline) {
    return (
      <div 
        className={`offline-indicator ${className}`}
        style={offlineStyles}
        role="alert"
        aria-live="assertive"
      >
        <StatusIcon isOnline={false} />
        You're offline. Some features may not work.
      </div>
    );
  }

  if (wasOffline) {
    return (
      <div 
        className={`online-indicator ${className}`}
        style={onlineStyles}
        role="status"
        aria-live="polite"
      >
        <StatusIcon isOnline={true} />
        Back online! Syncing data...
      </div>
    );
  }

  return null;
};

// Hook to get network status
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        // Trigger sync when coming back online
        console.log('Network restored, triggering sync...');
        setWasOffline(false);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  return { 
    isOnline, 
    wasOffline,
    // Helper method to check if we should show offline behavior
    isOfflineMode: !isOnline
  };
};

export default OfflineIndicator;